import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Building, Calendar, CheckCircle, Clock, Filter } from 'lucide-react';
import { projects } from '../mock';

const Projects = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  const categories = [
    { id: 'all', label: 'Tous les Projets' },
    { id: 'Audit QoS', label: 'Audit QoS' },
    { id: 'Contrôle Réseau', label: 'Contrôle Réseau' },
    { id: 'Digitalisation', label: 'Digitalisation' }
  ];
  
  const filteredProjects = selectedCategory === 'all' 
    ? projects 
    : projects.filter(project => project.category === selectedCategory);
    
  const getStatusColor = (status) => {
    switch (status) {
      case 'En cours': return 'bg-blue-100 text-blue-800';
      case 'Terminé': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'En cours': return <Clock className="w-4 h-4" />;
      case 'Terminé': return <CheckCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  return (
    <section id="projects" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
            Nos Réalisations
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
            Projets & Références
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Découvrez nos projets récents et nos partenariats avec les acteurs clés 
            du secteur des télécommunications en Afrique.
          </p>
        </div>

        {/* Category Filter */}
        <div className="mb-12">
          <div className="flex flex-wrap justify-center gap-3">
            <Filter className="w-5 h-5 text-slate-500 mt-2.5 mr-2" />
            {categories.map((category) => (
              <Button
                key={category.id}
                variant={selectedCategory === category.id ? "default" : "outline"}
                onClick={() => setSelectedCategory(category.id)}
                className={`transition-all duration-200 ${
                  selectedCategory === category.id 
                    ? 'bg-orange-600 hover:bg-orange-700 text-white' 
                    : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                {category.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Projects Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {filteredProjects.map((project) => (
            <Card key={project.id} className="group hover:shadow-xl transition-all duration-300 border border-slate-200 hover:border-orange-200">
              <CardHeader className="pb-4">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                      <Building className="w-6 h-6 text-orange-600" />
                    </div>
                    <div>
                      <Badge 
                        className={`${getStatusColor(project.status)} flex items-center space-x-1`}
                      >
                        {getStatusIcon(project.status)}
                        <span>{project.status}</span>
                      </Badge>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-slate-900">{project.year}</div>
                    <div className="text-xs text-slate-500">{project.category}</div>
                  </div>
                </div>
                
                <CardTitle className="text-xl font-bold text-slate-900 group-hover:text-orange-600 transition-colors">
                  {project.title}
                </CardTitle>
                
                <CardDescription className="flex items-center space-x-2 text-slate-600">
                  <Building className="w-4 h-4" />
                  <span className="font-medium">Client: {project.client}</span>
                </CardDescription>
              </CardHeader>
              
              <CardContent>
                <p className="text-slate-600 leading-relaxed mb-4">
                  {project.description}
                </p>
                
                <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                  <div className="flex items-center space-x-2 text-sm text-slate-500">
                    <Calendar className="w-4 h-4" />
                    <span>Année {project.year}</span>
                  </div>
                  
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                    onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    Détails du projet
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        
        {/* Client Testimonial */}
        <div className="bg-slate-900 rounded-2xl p-8 lg:p-12">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-8">
              <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Building className="w-10 h-10 text-orange-600" />
              </div>
              <blockquote className="text-xl lg:text-2xl text-white font-medium leading-relaxed mb-6">
                "SET-G a démontré une expertise remarquable dans l'audit de nos réseaux mobiles. 
                Leur approche méthodique et leurs recommandations ont considérablement amélioré 
                la qualité de service pour nos utilisateurs."
              </blockquote>
              <div className="text-slate-300">
                <div className="font-semibold">Direction Technique</div>
                <div className="text-sm">ARTCI - Autorité de Régulation des Télécommunications</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Projects;